import { z } from 'zod';
import { PopulatedUserSchema } from '../user/user.schema';
import { PopulatedDepartmentSchema } from '../department/department.schema';
import { OrgFunctionKindSchema } from '../org-function/org-function-ref.schema';
import { StepActionTypeSchema } from '../../shared/step-action-type.schema';
import { ObjectIdSchema } from '../../shared/object-id.schema';
import { ErrorCode } from '../../shared/error-codes';
/**
 * Suggestion kind.
 *
 * Only `system` and `orgFunction` go through the suggestion/review workflow
 * because they are tenant-governed entities (shadow-IT visibility for systems,
 * org-ownership for OrgFunctions). A `orgFunction` suggestion can propose a OrgFunction
 * of either inner kind (team or role) — that distinction lives in the payload's
 * own `kind`, not here. External contacts and artifacts are created directly via
 * add-external-contact / add-artifact — they don't carry the same governance
 * requirement, so the picker UI offers create-on-the-fly without an approval queue.
 */ export const EntitySuggestionKindSchema = z.enum([
    'system',
    'orgFunction'
]);
export const EntitySuggestionStatusSchema = z.enum([
    'pending',
    'approved',
    'merged',
    'rejected'
]);
export const SystemSuggestionPayloadSchema = z.object({
    // Trim before min(1): the suggestion dialogs bind these payload schemas
    // directly as their form resolver, so a whitespace-only name must fail
    // inline here — otherwise it passes the form, gets trimmed to '' on submit,
    // and the deferred-confirm flow retries the resulting 400 forever.
    name: z.string().trim().min(1, ErrorCode.FIELD_REQUIRED),
    description: z.string().optional(),
    category: z.string().optional(),
    url: z.string().optional(),
    systemOwner: z.string().optional(),
    licenceSeats: z.number().int().min(0).optional()
});
/**
 * OrgFunction suggestion payload.
 *
 * Carries the team/role distinction in its own `kind` — a not-yet-existing
 * OrgFunction of EITHER kind can be proposed. Only `name` + `kind` are required:
 * `department`, `lead`, `email`, and `description` are optional on both kinds
 * (mirroring the OrgFunction entity itself). `members[]` are user ids fanned out
 * into each user's `secondaryRoles` when the suggestion is approved.
 *
 * `department` is an ObjectId string when set, not a name — the AI tool once
 * stored raw department names here, which 500ed every read that casts this into
 * a Department._id $in query. `department`/`lead`/`members[]` are validated as
 * 24-hex ObjectIds at the wire boundary so a malformed id fails the request
 * instead of exploding in `ObjectId.createFromHexString` mid-transaction.
 */ export const OrgFunctionSuggestionPayloadSchema = z.object({
    // Trimmed for the same reason as the system payload name above.
    name: z.string().trim().min(1, ErrorCode.FIELD_REQUIRED),
    kind: OrgFunctionKindSchema,
    description: z.string().optional(),
    department: ObjectIdSchema.optional(),
    email: z.email().optional(),
    lead: ObjectIdSchema.optional(),
    members: z.array(ObjectIdSchema).optional()
});
const Base = z.object({
    _id: z.string(),
    status: EntitySuggestionStatusSchema,
    submittedBy: z.string(),
    submittedAt: z.string(),
    /**
   * The source NODE's id in the unified node collection (ADR 0003 /
   * issue 11) — a step recording (the historical default) or a start event
   * node (what the start-event picker writes since the anchor unification;
   * the node lives in `processsteprecordings`, so the name still holds).
   * Optional only because a recording can be hard-deleted out from under a
   * suggestion mid-rehome — every create path sets it.
   */ sourceRecordingId: z.string().optional(),
    /**
   * Set when the anchor is a draft node's `stableStepId`; reviewer visibility
   * then follows the draft's publish, never the live node's lifecycle.
   * Absent means live — legacy rows are live by absence, so there is no
   * migration.
   */ sourceScope: z.literal('draft').optional(),
    /**
   * The draft this suggestion was raised in; present only with
   * `sourceScope: 'draft'`, and cleared with it at publish.
   */ sourceDraftId: z.string().optional(),
    reviewedBy: z.string().optional(),
    reviewedAt: z.string().optional(),
    rejectionReason: z.string().optional(),
    resolvedEntityId: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string()
});
export const SystemEntitySuggestionSchema = Base.extend({
    kind: z.literal('system'),
    payload: SystemSuggestionPayloadSchema
});
export const OrgFunctionEntitySuggestionSchema = Base.extend({
    kind: z.literal('orgFunction'),
    payload: OrgFunctionSuggestionPayloadSchema
});
export const EntitySuggestionSchema = z.discriminatedUnion('kind', [
    SystemEntitySuggestionSchema,
    OrgFunctionEntitySuggestionSchema
]);
const PopulatedBase = Base.extend({
    submittedBy: PopulatedUserSchema,
    reviewedBy: PopulatedUserSchema.optional(),
    /**
   * Action type of the source step recording, denormalized onto the populated
   * suggestion so approver UIs can render task/decision icons without a per-row
   * recording fetch. Optional because a recording may be deleted out from under
   * a still-pending suggestion, and start-event suggestions have no source recording at all.
   */ sourceRecordingActionType: StepActionTypeSchema.optional(),
    /**
   * Human-readable label of the source step recording (generatedName, falling
   * back to task.taskDescription). Denormalized so list/table UIs can show the
   * originating step without a per-row recording fetch. Optional for the same
   * reasons as sourceRecordingActionType.
   */ sourceRecordingStepLabel: z.string().optional(),
    /**
   * Human-readable label of the source process (process.name). Set for both
   * step-sourced suggestions (resolved via the source recording's `process`
   * ref) and start-event-node-anchored ones (resolved via the node's
   * `process`), so reviewer UIs can render the originating process without a
   * per-row process fetch. Optional because the resolver no-ops when no
   * process repository is supplied (e.g. shadow-system listing).
   */ sourceProcessLabel: z.string().optional(),
    /**
   * Id of the source process, set ONLY for start-event-anchored suggestions
   * (`sourceRecordingId` names a start event node). Derived by the same
   * population pass as `sourceProcessLabel` — the document itself stores no
   * process anchor. Presence doubles as the "raised on the start event, not
   * inside a step" discriminator for reviewer UIs, and lets process-keyed
   * readers (the process preview dialog) open the originating process
   * without resolving the node.
   */ sourceProcessId: z.string().optional()
});
export const PopulatedSystemEntitySuggestionSchema = PopulatedBase.extend({
    kind: z.literal('system'),
    payload: SystemSuggestionPayloadSchema
});
export const PopulatedOrgFunctionEntitySuggestionSchema = PopulatedBase.extend({
    kind: z.literal('orgFunction'),
    // `department` is optional on a OrgFunction suggestion; when present it is
    // populated to a department object after toJSON (the raw payload stores an
    // ObjectId string, which Mongoose can't auto-populate inside Mixed).
    payload: OrgFunctionSuggestionPayloadSchema.extend({
        department: PopulatedDepartmentSchema.optional()
    })
});
export const PopulatedEntitySuggestionSchema = z.discriminatedUnion('kind', [
    PopulatedSystemEntitySuggestionSchema,
    PopulatedOrgFunctionEntitySuggestionSchema
]);

//# sourceMappingURL=entity-suggestion.schema.js.map